import React from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { getSettings } from "src/services/configService";
import { useSelector } from "src/store";
import { selectIsWorkspaceResolved } from "src/store/app";
import { selectHasValidSession } from "src/store/auth";
import { useEnrollmentFlowEnabled } from "src/ui/containers/dashboard/enrollment/utils/useEnrollmentFlowEnabled";
import { isDashboardPageAccessible } from "src/ui/navigation/dashboardRouting/dashboardRouting";
import { useRecommendedPathForUser } from "src/ui/navigation/dashboardRouting/useRecommendedPathForUser";

export const PageGuard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;
    const { features } = getSettings();
    const { effectiveWorkspaceSettings, isLoading } = useAppSettingsService();
    const isEnrollmentFlowEnabled = useEnrollmentFlowEnabled();
    const hasValidSession = useSelector(selectHasValidSession);
    const isWorkspaceResolved = useSelector(selectIsWorkspaceResolved);
    const { path: recommendedPath, isLoading: isRecommendedPathLoading } = useRecommendedPathForUser();
    const accessContext = { effective: effectiveWorkspaceSettings, pages: features.pages };

    const isEnrollmentFlowPage = pathname === "/dashboard/enrollment" && isEnrollmentFlowEnabled;
    const isOssLicensesPageDisabled = pathname === "/embedded/third-party" && !features.pages.ossLicensesPageEnabled;
    const isAccessible = isDashboardPageAccessible(pathname, accessContext);

    const isIndexPath = pathname === "/" || pathname === "/dashboard";
    const honorsRecommendation =
        (isIndexPath || isAccessible === false) && hasValidSession && !isEnrollmentFlowPage && !isOssLicensesPageDisabled;
    const isRecommendationKnown = isWorkspaceResolved && !isRecommendedPathLoading;
    const recommendationRedirect =
        honorsRecommendation && isRecommendationKnown && recommendedPath !== pathname ? recommendedPath : null;

    // Redirect from an effect while keeping the Outlet rendered: before the workspace is resolved the subtree below
    // is already mounted (PrefetchGuard is the one resolving it), and swapping the Outlet for a <Navigate> would
    // unmount it and send every startup request a second time.
    React.useEffect(() => {
        if (recommendationRedirect) {
            void navigate(recommendationRedirect, { replace: true });
        }
    }, [recommendationRedirect, navigate]);

    if (pathname === "/register" && !features.registrationEnabled) {
        return <Navigate to="/login" replace />;
    }

    if (isEnrollmentFlowPage) {
        return <Outlet />;
    }

    if (isOssLicensesPageDisabled) {
        return <Navigate to="/forbidden" replace />;
    }

    if (honorsRecommendation) {
        return <Outlet />;
    }

    if (isAccessible === null) {
        return <Outlet />;
    }

    if (isLoading || isAccessible) {
        return <Outlet />;
    }

    return <Navigate to="/dashboard/overview" replace />;
};
